import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { deleteObject, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { Spinner } from '../components/ui';
import BiomarkerEntryRow from '../components/member/BiomarkerEntryRow';
import { LabResultRecord } from '../types/labs';
import { resolveBiomarker, type BiomarkerDomain } from '../data/biomarkerCatalogue';
import { computeBiomarkerStatus } from '../utils/biomarkerStatus';
import { viewStoredFile } from '../services/secureFileAccess';
import { filterVisibleToMember } from '../utils/labResultVisibility';
import { listResultDraws } from '../utils/dashboard/newResults';
import ResultsDrawHeader from '../components/member/ResultsDrawHeader';

/**
 * Whether to show explanations that have not yet been clinician-approved.
 *
 * NOW ON BY DEFAULT, by explicit product decision (2026-08-12): members were
 * reading a flagged number like "ApoB 1.42 g/L [High]" with nothing telling
 * them what ApoB is, and understanding the result was judged to outweigh
 * waiting for sign-off. Set REACT_APP_SHOW_DRAFT_BIOMARKER_COPY=false to
 * withhold them again.
 *
 * It defaults on in committed code rather than via an env file for the same
 * reason DASHBOARD_V2 had to: `.env.*` is gitignored and there is no
 * .env.production, so a `=== 'true'` check could never be true in a production
 * build. The flag read as "off in prod until approved" but behaved as "off in
 * prod forever".
 *
 * The catalogue entries deliberately remain `copyStatus: 'draft'`. Marking them
 * 'approved' would record a clinician sign-off in the codebase that has not
 * happened; the copy is still pending review in
 * docs/clinical/BIOMARKER_COPY_SIGNOFF.md, and this constant is the record that
 * we chose to display it anyway. As markers are genuinely signed off they flip
 * to 'approved' individually and stop depending on this at all
 * (scripts/biomarker-copy-review.js exports the review sheet).
 */
const SHOW_DRAFT_BIOMARKER_COPY = process.env.REACT_APP_SHOW_DRAFT_BIOMARKER_COPY !== 'false';

interface UploadedFile {
  id: string;
  fileName: string;
  storagePath: string;
  uploadedAt: unknown;
}

const MEDICAL_DOC_ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic';
const WEARABLE_ACCEPT = '.json,.csv,.xml,.zip';
const BIOMARKER_OPTIONS = ['ApoB', 'Lp(a)', 'HbA1c', 'hs-CRP', 'LDL-C', 'HDL-C', 'Triglycerides', 'Fasting glucose', 'Fasting insulin', 'Ferritin', 'ALT', 'Creatinine'];

const formatDate = (value?: unknown) => {
  if (!value) return '—';
  const date = (value as { toDate?: () => Date }).toDate?.() ?? new Date(value as string);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Domain display order for the grouped biomarker list (clinical priority first).
const DOMAIN_ORDER: BiomarkerDomain[] = [
  'cardiovascular', 'metabolic', 'liver', 'renal', 'iron',
  'vitamins', 'thyroid', 'cbc', 'electrolytes', 'reproductive',
];
const TIER_ORDER: Record<string, number> = { CORE: 0, BASELINE: 1, CONTEXT: 2, OPTIONAL: 3 };

/**
 * The body of `/results` — see MemberResultsPage, its only consumer.
 *
 * The name is historical and describes what this component WRITES (uploads,
 * manual biomarker entry) rather than what it now leads with. Since the draw
 * restructure it opens on the member's own markers; the filing is collapsed
 * beneath them.
 */
export default function DataUploadsPage({ embedded = false }: { embedded?: boolean }) {
  const { currentUser } = useAuth();
  const medicalInputRef = useRef<HTMLInputElement>(null);
  const wearableInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [uploadingMedical, setUploadingMedical] = useState(false);
  const [uploadingWearable, setUploadingWearable] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [medicalDocs, setMedicalDocs] = useState<UploadedFile[]>([]);
  const [wearableDocs, setWearableDocs] = useState<UploadedFile[]>([]);
  const [labResults, setLabResults] = useState<LabResultRecord[]>([]);
  const [memberSex, setMemberSex] = useState<string | null>(null);
  const [savingLabResult, setSavingLabResult] = useState(false);
  const [editingLabResultId, setEditingLabResultId] = useState<string | null>(null);
  const [labForm, setLabForm] = useState({
    biomarker: 'ApoB',
    value: '',
    unit: '',
    observedAt: '',
    sourceDocumentName: '',
    notes: '',
  });

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      try {
        const [medicalSnap, wearableSnap, labResultsSnap, userSnap] = await Promise.all([
          getDocs(collection(db, 'users', currentUser.uid, 'medical_documents')),
          getDocs(collection(db, 'users', currentUser.uid, 'wearable_data')),
          getDocs(collection(db, 'users', currentUser.uid, 'lab_results')),
          // Sex resolves sex-specific reference ranges (HDL, haemoglobin, ferritin, …).
          getDoc(doc(db, 'users', currentUser.uid)).catch(() => null),
        ]);

        const userData = userSnap && userSnap.exists() ? (userSnap.data() as Record<string, unknown>) : {};
        setMemberSex(
          (userData.sexAtBirth as string) || (userData.sex as string) || (userData.gender as string) || null,
        );

        setMedicalDocs(
          medicalSnap.docs.map((snapshot) => ({
            id: snapshot.id,
            ...(snapshot.data() as Omit<UploadedFile, 'id'>),
          }))
        );
        setWearableDocs(
          wearableSnap.docs.map((snapshot) => ({
            id: snapshot.id,
            ...(snapshot.data() as Omit<UploadedFile, 'id'>),
          }))
        );
        // Held results (`awaiting_review`) are filtered out here too. This page
        // reads the legacy collection directly because it also edits and deletes
        // by document id, but the member-facing gate applies the same either way.
        setLabResults(
          filterVisibleToMember(labResultsSnap.docs.map((snapshot) => ({
            id: snapshot.id,
            ...(snapshot.data() as Omit<LabResultRecord, 'id'>),
          })))
        );
      } catch (error) {
        console.error('Error loading uploads:', error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currentUser]);

  const uploadMedicalFile = async (file: File) => {
    if (!currentUser) return;
    if (file.size > 10 * 1024 * 1024) {
      window.alert('Medical documents must be under 10MB.');
      return;
    }

    setUploadingMedical(true);
    try {
      const storagePath = `medical_documents/${currentUser.uid}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      // SEC-3: persist only the storagePath. We deliberately do NOT call
      // getDownloadURL() — that mints a permanent, rule-bypassing token. The
      // file is fetched on demand via the authenticated getBlob() path
      // (viewStoredFile), which storage.rules governs.
      const docRef = await addDoc(collection(db, 'users', currentUser.uid, 'medical_documents'), {
        fileName: file.name,
        storagePath,
        uploadedAt: serverTimestamp(),
        sourceSystem: 'member_portal',
      });

      setMedicalDocs((current) => [
        {
          id: docRef.id,
          fileName: file.name,
          storagePath,
          uploadedAt: new Date(),
        },
        ...current,
      ]);
      if (medicalInputRef.current) medicalInputRef.current.value = '';
    } catch (error) {
      console.error('Error uploading medical document:', error);
      window.alert('Failed to upload the document. Please try again.');
    } finally {
      setUploadingMedical(false);
    }
  };

  const uploadWearableFile = async (file: File) => {
    if (!currentUser) return;
    if (file.size > 50 * 1024 * 1024) {
      window.alert('Wearable exports must be under 50MB.');
      return;
    }

    setUploadingWearable(true);
    try {
      const storagePath = `wearable_data/${currentUser.uid}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      // SEC-3: persist only the storagePath (see uploadMedicalFile) — no
      // permanent getDownloadURL() token. The onObjectFinalized wearable
      // parser reads the object server-side via storagePath, unaffected.
      const docRef = await addDoc(collection(db, 'users', currentUser.uid, 'wearable_data'), {
        fileName: file.name,
        storagePath,
        uploadedAt: serverTimestamp(),
        sourceType: file.name.endsWith('.zip')
          ? 'whoop_zip'
          : file.name.endsWith('.json')
            ? 'json'
            : file.name.endsWith('.csv')
              ? 'csv'
              : 'xml',
        sourceSystem: 'member_portal',
      });

      setWearableDocs((current) => [
        {
          id: docRef.id,
          fileName: file.name,
          storagePath,
          uploadedAt: new Date(),
        },
        ...current,
      ]);
      if (wearableInputRef.current) wearableInputRef.current.value = '';
    } catch (error) {
      console.error('Error uploading wearable export:', error);
      window.alert('Failed to upload the wearable export. Please try again.');
    } finally {
      setUploadingWearable(false);
    }
  };

  const removeFile = async (collectionName: 'medical_documents' | 'wearable_data', file: UploadedFile) => {
    if (!currentUser) return;
    if (!window.confirm(`Delete ${file.fileName}?`)) return;

    setDeletingId(file.id);
    try {
      await deleteObject(ref(storage, file.storagePath));
      await deleteDoc(doc(db, 'users', currentUser.uid, collectionName, file.id));

      if (collectionName === 'medical_documents') {
        setMedicalDocs((current) => current.filter((item) => item.id !== file.id));
      } else {
        setWearableDocs((current) => current.filter((item) => item.id !== file.id));
      }
    } catch (error) {
      console.error('Error deleting upload:', error);
      window.alert('Failed to delete the file. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleView = async (file: UploadedFile) => {
    setViewingId(file.id);
    try {
      await viewStoredFile(file.storagePath);
    } catch (error) {
      console.error('Error opening file:', error);
      window.alert('Could not open the file. Please try again.');
    } finally {
      setViewingId(null);
    }
  };

  const resetLabForm = () => {
    setLabForm({
      biomarker: 'ApoB',
      value: '',
      unit: '',
      observedAt: '',
      sourceDocumentName: '',
      notes: '',
    });
    setEditingLabResultId(null);
  };

  const saveLabResult = async () => {
    if (!currentUser || !labForm.value.trim()) return;

    setSavingLabResult(true);
    try {
      const payload = {
        biomarker: labForm.biomarker,
        value: labForm.value.trim(),
        unit: labForm.unit.trim() || null,
        observedAt: labForm.observedAt || null,
        sourceDocumentName: labForm.sourceDocumentName || null,
        notes: labForm.notes.trim() || null,
        sourceType: 'manual_lab_entry',
        sourceSystem: 'member_portal',
        reviewState: 'member_entered',
        schemaVersion: 1,
        modelVersion: '2026-03-data-foundation-v1',
        updatedAt: serverTimestamp(),
      };

      if (editingLabResultId) {
        await updateDoc(doc(db, 'users', currentUser.uid, 'lab_results', editingLabResultId), payload);
        setLabResults((current) =>
          current.map((result) => (result.id === editingLabResultId ? { ...result, ...payload } : result))
        );
      } else {
        const docRef = await addDoc(collection(db, 'users', currentUser.uid, 'lab_results'), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setLabResults((current) => [{ id: docRef.id, ...payload, createdAt: new Date() }, ...current]);
      }

      resetLabForm();
    } catch (error) {
      console.error('Error saving lab result:', error);
      window.alert('Failed to save biomarker value. Please try again.');
    } finally {
      setSavingLabResult(false);
    }
  };

  const latestLabResults = useMemo(() => {
    return [...labResults].sort((a, b) => {
      const aTime = a.observedAt ? new Date(a.observedAt).getTime() : 0;
      const bTime = b.observedAt ? new Date(b.observedAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [labResults]);

  // Group the newest reading per biomarker by domain (clinical priority order),
  // with out-of-range markers surfaced first, plus an at-a-glance summary count.
  const { biomarkerGroups, outOfRangeCount, judgedCount } = useMemo(() => {
    const latestPerMarker = new Map<string, LabResultRecord>();
    for (const result of latestLabResults) {
      const meta = resolveBiomarker({ biomarkerKey: result.biomarkerKey, biomarker: result.biomarker });
      const key = meta ? meta.key : `label:${(result.biomarker || '').toLowerCase()}`;
      if (!latestPerMarker.has(key)) latestPerMarker.set(key, result); // first = newest (list is desc)
    }

    type Item = { result: LabResultRecord; tier: string; statusRank: number };
    const groups = new Map<string, { domain: string; label: string; order: number; items: Item[] }>();
    let outOfRange = 0;
    let judged = 0;

    for (const result of latestPerMarker.values()) {
      const meta = resolveBiomarker({ biomarkerKey: result.biomarkerKey, biomarker: result.biomarker });
      const domain = meta ? meta.domain : 'other';
      const label = meta ? meta.domainLabel : 'Other markers';
      const order = meta ? DOMAIN_ORDER.indexOf(meta.domain) : 999;
      let statusRank = 3;
      if (meta) {
        const status = computeBiomarkerStatus(result, meta, memberSex);
        if (status.status !== 'unknown') {
          judged += 1;
          if (status.status === 'high' || status.status === 'low') {
            outOfRange += 1;
            statusRank = 0;
          } else {
            statusRank = 1;
          }
        }
      }
      const group = groups.get(domain) || { domain, label, order: order < 0 ? 998 : order, items: [] };
      group.items.push({ result, tier: meta ? meta.tier : 'OPTIONAL', statusRank });
      groups.set(domain, group);
    }

    const ordered = [...groups.values()].sort((a, b) => a.order - b.order);
    ordered.forEach((group) =>
      group.items.sort((a, b) => a.statusRank - b.statusRank || (TIER_ORDER[a.tier] ?? 3) - (TIER_ORDER[b.tier] ?? 3)),
    );
    return { biomarkerGroups: ordered, outOfRangeCount: outOfRange, judgedCount: judged };
  }, [latestLabResults, memberSex]);

  // The draw the member is most likely asking about. The list below still shows
  // the newest reading for every marker on file — a member whose latest draw was
  // a three-marker recheck should not lose sight of the other forty — so the
  // draw is a header over that list rather than a filter on it, and the rows it
  // contains are tagged instead.
  const draws = useMemo(() => listResultDraws(labResults, memberSex), [labResults, memberSex]);
  const latestDraw = draws[0] || null;
  const markersOnFile = useMemo(
    () => biomarkerGroups.reduce((total, group) => total + group.items.length, 0),
    [biomarkerGroups],
  );

  /**
   * Rows to tag "New" — the ones that came back on the latest draw.
   *
   * Empty when EVERY displayed row would be tagged, because then the tag
   * distinguishes nothing; it is 59 identical stickers on a list of 59. That
   * happens in two ordinary cases: a member with a single panel, and a member
   * whose repeat panels cover the same markers every time, so the newest
   * reading of each is always from the latest draw. Real staging data is the
   * second, which is why the first-pass rule (tag whenever more than one draw
   * exists) was not enough.
   *
   * The tag earns its place only when the latest draw is a PART of what is on
   * screen — a recheck of three markers among forty.
   */
  const latestDrawIds = useMemo(() => {
    const empty = new Set<string>();
    if (!latestDraw) return empty;
    const candidate = new Set(latestDraw.recordIds);
    const displayed = biomarkerGroups.flatMap((group) => group.items.map((item) => item.result.id));
    const tagged = displayed.filter((id) => candidate.has(id)).length;
    return tagged === displayed.length ? empty : candidate;
  }, [latestDraw, biomarkerGroups]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const content = (
    <>
      {/* The draw, first. This page was organised around how data arrived —
          "Reports and documents", "Wearables and exports", "Add biomarker
          values", and only then the member's own numbers, in the fourth
          section. A member who had just had blood taken was looking for an
          event that happened to them on a Tuesday and found a filing cabinet.
          Their results now open the page and uploading is an errand at the
          bottom of it. */}
      {latestDraw && (
        <ResultsDrawHeader
          draw={latestDraw}
          isFirstDraw={draws.length === 1}
          totalMarkersOnFile={markersOnFile}
          tagsShown={latestDrawIds.size > 0}
        />
      )}

      <section className="mb-8">
        <div className="rounded-3xl border p-6" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}>
          <h2
            className="text-2xl font-normal mb-3"
            style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
          >
            Your markers
          </h2>
          <p className="text-sm mb-4" style={{ color: 'var(--melio-text-muted)' }}>
            The most recent reading for each marker on your record, grouped by what it tells us about.
            Select any marker to see where you sit and what it means.
          </p>

          {/* At-a-glance summary. Suppressed when a draw header is shown: it
              states the same counts a few hundred pixels higher, and two
              differently-scoped totals on one screen ("24 markers came back" vs
              "of 51 assessed") read as a contradiction rather than as two
              facts. */}
          {judgedCount > 0 && !latestDraw && (
            <div
              className="rounded-2xl mb-5 px-4 py-3 flex items-center gap-2 flex-wrap"
              style={{
                backgroundColor: outOfRangeCount > 0 ? 'rgba(155, 47, 42, 0.08)' : 'rgba(79, 93, 24, 0.1)',
              }}
            >
              <span
                className="inline-flex items-center rounded-full text-[11px] font-semibold"
                style={{
                  color: outOfRangeCount > 0 ? '#9B2F2A' : '#4F5D18',
                  backgroundColor: outOfRangeCount > 0 ? 'rgba(155, 47, 42, 0.14)' : 'rgba(79, 93, 24, 0.18)',
                  padding: '2px 10px',
                }}
              >
                {outOfRangeCount > 0 ? `${outOfRangeCount} outside range` : 'All in range'}
              </span>
              <span className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                {outOfRangeCount > 0
                  ? `of ${judgedCount} markers assessed against your reference ranges — your care team reviews these in your report.`
                  : `across ${judgedCount} markers assessed against your reference ranges.`}
              </span>
            </div>
          )}

          {biomarkerGroups.length > 0 ? (
            <div className="space-y-6">
              {biomarkerGroups.map((group) => (
                <div key={group.domain}>
                  <p
                    className="text-[10px] uppercase tracking-[0.18em] mb-2 pb-1"
                    style={{ color: 'var(--melio-olive-deep, #4F5D18)', borderBottom: '1px solid var(--melio-border)' }}
                  >
                    {group.label}
                  </p>
                  <div className="space-y-3">
                    {group.items.map(({ result }) => (
                      <BiomarkerEntryRow
                        key={result.id}
                        result={result}
                        allResults={labResults}
                        sex={memberSex}
                        showDraftCopy={SHOW_DRAFT_BIOMARKER_COPY}
                        formatDate={formatDate}
                        isFromLatestDraw={latestDrawIds.has(result.id)}
                        onEdit={(r) => {
                          setEditingLabResultId(r.id);
                          setLabForm({
                            biomarker: r.biomarker,
                            value: r.value,
                            unit: r.unit || '',
                            observedAt: r.observedAt || '',
                            sourceDocumentName: r.sourceDocumentName || '',
                            notes: r.notes || '',
                          });
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
              <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                No biomarker values entered yet.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Uploading, manual entry and the explainer cards are all things a
          member DOES to their record rather than things their record says, so
          they collapse. Closed by default: after a panel comes back, the
          overwhelming majority of visits to this page are to read, not to
          file. */}
      <details className="mb-8 border" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}>
        <summary
          className="px-6 py-4 cursor-pointer text-sm font-medium select-none"
          style={{ color: 'var(--melio-charcoal)' }}
        >
          Add to your record — upload a report, connect an export, or enter a value
        </summary>
        <div className="px-6 pb-6 pt-2">
      <section className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="rounded-3xl border p-6" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}>
          <h2
            className="text-2xl font-normal mb-3"
            style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
          >
            Reports and documents
          </h2>
          <p className="text-sm mb-5" style={{ color: 'var(--melio-text-muted)' }}>
            Use this for bloods, genetics PDFs, clinician letters, imaging summaries, and anything else your care team
            should consider.
          </p>
          <input
            ref={medicalInputRef}
            type="file"
            accept={MEDICAL_DOC_ACCEPT}
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) uploadMedicalFile(file);
            }}
          />
          <button
            type="button"
            onClick={() => medicalInputRef.current?.click()}
            disabled={uploadingMedical}
            className="px-5 py-3 rounded-lg font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
          >
            {uploadingMedical ? 'Uploading...' : 'Upload report'}
          </button>
          <div className="mt-5 space-y-3">
            {medicalDocs.length > 0 ? (
              medicalDocs.map((file) => (
                <div
                  key={file.id}
                  className="rounded-2xl border p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
                  style={{ borderColor: 'var(--melio-border)' }}
                >
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => handleView(file)}
                      disabled={viewingId === file.id}
                      className="font-medium hover:opacity-80 block truncate text-left disabled:opacity-60"
                      style={{ color: 'var(--melio-charcoal)' }}
                    >
                      {viewingId === file.id ? 'Opening…' : file.fileName}
                    </button>
                    <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                      Uploaded {formatDate(file.uploadedAt)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile('medical_documents', file)}
                    disabled={deletingId === file.id}
                    className="px-4 py-2 rounded-lg border text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
                    style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}
                  >
                    {deletingId === file.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              ))
            ) : (
              <div className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
                <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                  No reports uploaded yet.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-3xl border p-6" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}>
          <h2
            className="text-2xl font-normal mb-3"
            style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
          >
            Wearables and exports
          </h2>
          <p className="text-sm mb-5" style={{ color: 'var(--melio-text-muted)' }}>
            Upload exports from Whoop, Oura, Apple Health, Garmin, Fitbit, or other services. Melio can use these to
            add sleep, recovery, and activity context around your plan.
          </p>
          <input
            ref={wearableInputRef}
            type="file"
            accept={WEARABLE_ACCEPT}
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) uploadWearableFile(file);
            }}
          />
          <button
            type="button"
            onClick={() => wearableInputRef.current?.click()}
            disabled={uploadingWearable}
            className="px-5 py-3 rounded-lg font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
          >
            {uploadingWearable ? 'Uploading...' : 'Upload wearable export'}
          </button>
          <div className="mt-5 space-y-3">
            {wearableDocs.length > 0 ? (
              wearableDocs.map((file) => (
                <div
                  key={file.id}
                  className="rounded-2xl border p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
                  style={{ borderColor: 'var(--melio-border)' }}
                >
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => handleView(file)}
                      disabled={viewingId === file.id}
                      className="font-medium hover:opacity-80 block truncate text-left disabled:opacity-60"
                      style={{ color: 'var(--melio-charcoal)' }}
                    >
                      {viewingId === file.id ? 'Opening…' : file.fileName}
                    </button>
                    <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                      Uploaded {formatDate(file.uploadedAt)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile('wearable_data', file)}
                    disabled={deletingId === file.id}
                    className="px-4 py-2 rounded-lg border text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
                    style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}
                  >
                    {deletingId === file.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              ))
            ) : (
              <div className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
                <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                  No wearable exports uploaded yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

          <section className="mb-8">

        <div className="rounded-3xl border p-6" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}>
          <h2
            className="text-2xl font-normal mb-3"
            style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
          >
            Add biomarker values
          </h2>
          <p className="text-sm mb-5" style={{ color: 'var(--melio-text-muted)' }}>
            After uploading a report, add the key values you want to track. This gives the dashboard real numbers straight
            away without waiting for a future OCR pipeline.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
                Biomarker
              </label>
              <select
                value={labForm.biomarker}
                onChange={(event) => setLabForm((current) => ({ ...current, biomarker: event.target.value }))}
                className="w-full px-4 py-3 rounded-lg border"
                style={{ borderColor: 'var(--melio-border)' }}
              >
                {BIOMARKER_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
                Value
              </label>
              <input
                value={labForm.value}
                onChange={(event) => setLabForm((current) => ({ ...current, value: event.target.value }))}
                className="w-full px-4 py-3 rounded-lg border"
                style={{ borderColor: 'var(--melio-border)' }}
                placeholder="e.g. 0.82"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
                Unit
              </label>
              <input
                value={labForm.unit}
                onChange={(event) => setLabForm((current) => ({ ...current, unit: event.target.value }))}
                className="w-full px-4 py-3 rounded-lg border"
                style={{ borderColor: 'var(--melio-border)' }}
                placeholder="e.g. g/L, mmol/L, mg/L"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
                Date observed
              </label>
              <input
                type="date"
                value={labForm.observedAt}
                onChange={(event) => setLabForm((current) => ({ ...current, observedAt: event.target.value }))}
                className="w-full px-4 py-3 rounded-lg border"
                style={{ borderColor: 'var(--melio-border)' }}
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
              Source report
            </label>
            <select
              value={labForm.sourceDocumentName}
              onChange={(event) => setLabForm((current) => ({ ...current, sourceDocumentName: event.target.value }))}
              className="w-full px-4 py-3 rounded-lg border"
              style={{ borderColor: 'var(--melio-border)' }}
            >
              <option value="">Select uploaded report</option>
              {medicalDocs.map((file) => (
                <option key={file.id} value={file.fileName}>{file.fileName}</option>
              ))}
            </select>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
              Notes
            </label>
            <textarea
              rows={3}
              value={labForm.notes}
              onChange={(event) => setLabForm((current) => ({ ...current, notes: event.target.value }))}
              className="w-full px-4 py-3 rounded-lg border"
              style={{ borderColor: 'var(--melio-border)' }}
              placeholder="Optional context such as fasting state or lab comments."
            />
          </div>
          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={saveLabResult}
              disabled={savingLabResult || !labForm.value.trim()}
              className="px-5 py-3 rounded-lg font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
            >
              {savingLabResult ? 'Saving...' : editingLabResultId ? 'Update biomarker' : 'Save biomarker'}
            </button>
            {editingLabResultId && (
              <button
                type="button"
                onClick={resetLabForm}
                className="px-5 py-3 rounded-lg border font-medium transition-opacity hover:opacity-80"
                style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
          </section>

      <section className="grid md:grid-cols-3 gap-4">
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'white', border: '1px solid var(--melio-border)' }}>
          <p className="font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>Best uploads to start with</p>
          <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
            Baseline bloods, genetics summary reports, a medication list, and any recent imaging or specialist letters.
          </p>
        </div>
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'white', border: '1px solid var(--melio-border)' }}>
          <p className="font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>How Melio uses this</p>
          <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
            Uploads support clinician review, prompt creation, and updates to your testing cadence rather than endless data hoarding.
          </p>
        </div>
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'white', border: '1px solid var(--melio-border)' }}>
          <p className="font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>What comes next</p>
          <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
            Once results are reviewed, head to Results to see plan changes, follow-up prompts, and recommended next steps.
          </p>
        </div>
      </section>
        </div>
      </details>
    </>
  );

  return embedded ? content : (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {content}
    </div>
  );
}
